// src/pages/Admin/UserAdmin/EmployeeHierarchyPage.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Container, Form, InputGroup, Pagination, Spinner, Table } from 'react-bootstrap';
import { FaExternalLinkAlt, FaIdBadge, FaSearch, FaSort, FaSortDown, FaSortUp, FaSyncAlt, FaUserTie } from 'react-icons/fa';
import { EmployeeHierarchyUser, getEmployeeHierarchyUsers, getUserDirectoryFilterOptions, PagedResult } from '@/api/userAdminApi';
import './SpAccountPurgePage.scss';

const PAGE_SIZE = 25;
type SortDirection = 'asc' | 'desc';

const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString() : '-');
const fmt = (d?: string) => (d ? new Date(d).toLocaleString() : '-');

function PaginationBar({
  page, pageSize, total, onChange,
}: {
  page: number; pageSize: number; total: number; onChange: (p: number) => void;
}) {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;
  return (
    <div className="d-flex justify-content-between align-items-center mt-3">
      <small className="text-muted">
        {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total}
      </small>
      <Pagination size="sm" className="mb-0">
        <Pagination.Prev disabled={page === 1} onClick={() => onChange(page - 1)} />
        {page > 2 && <Pagination.Item onClick={() => onChange(1)}>1</Pagination.Item>}
        {page > 3 && <Pagination.Ellipsis />}
        {page > 1 && <Pagination.Item onClick={() => onChange(page - 1)}>{page - 1}</Pagination.Item>}
        <Pagination.Item active>{page}</Pagination.Item>
        {page < totalPages && <Pagination.Item onClick={() => onChange(page + 1)}>{page + 1}</Pagination.Item>}
        {page < totalPages - 2 && <Pagination.Ellipsis />}
        {page < totalPages - 1 && <Pagination.Item onClick={() => onChange(totalPages)}>{totalPages}</Pagination.Item>}
        <Pagination.Next disabled={page === totalPages} onClick={() => onChange(page + 1)} />
      </Pagination>
    </div>
  );
}

function SortHeader({
  label, column, sortBy, sortDirection, onSort,
}: {
  label: string;
  column: string;
  sortBy: string;
  sortDirection: SortDirection;
  onSort: (column: string) => void;
}) {
  const active = sortBy === column;
  return (
    <button type="button" className="spa-sort-header" onClick={() => onSort(column)}>
      <span>{label}</span>
      {active ? (sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort />}
    </button>
  );
}

const EmployeeHierarchyPage: React.FC = () => {
  const [data, setData] = useState<PagedResult<EmployeeHierarchyUser> | null>(null);
  const [departmentOptions, setDepartmentOptions] = useState<string[]>([]);
  const [employeeTypeOptions, setEmployeeTypeOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [employeeType, setEmployeeType] = useState('');
  const [submittedEmployeeType, setSubmittedEmployeeType] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [submittedDepartmentId, setSubmittedDepartmentId] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getEmployeeHierarchyUsers(
        page,
        PAGE_SIZE,
        submittedSearch,
        submittedEmployeeType,
        submittedDepartmentId,
        sortBy,
        sortDirection
      ));
    } catch {
      setError('Failed to load employee hierarchy');
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, sortDirection, submittedDepartmentId, submittedEmployeeType, submittedSearch]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    getUserDirectoryFilterOptions()
      .then((options) => {
        if (cancelled) return;
        setDepartmentOptions(options.employeeDepartmentIds || []);
        setEmployeeTypeOptions(options.employeeTypes || []);
      })
      .catch(() => {
        if (cancelled) return;
        setDepartmentOptions([]);
        setEmployeeTypeOptions([]);
      });
    return () => { cancelled = true; };
  }, []);

  function applyFilters(e?: React.FormEvent) {
    e?.preventDefault();
    setPage(1);
    setSubmittedSearch(search.trim());
    setSubmittedEmployeeType(employeeType.trim());
    setSubmittedDepartmentId(departmentId.trim());
  }

  function clearFilters() {
    setSearch('');
    setSubmittedSearch('');
    setEmployeeType('');
    setSubmittedEmployeeType('');
    setDepartmentId('');
    setSubmittedDepartmentId('');
    setPage(1);
  }

  function sort(column: string) {
    setPage(1);
    if (sortBy === column) {
      setSortDirection((current) => current === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  }

  function openUser(identifier?: string) {
    if (!identifier) return;
    window.open(`/admin/users/details/${encodeURIComponent(identifier)}`, '_blank', 'noopener,noreferrer');
  }

  return (
    <Container className="mt-4 spa-page">
      <div className="mb-4">
        <h2 className="mb-1"><FaIdBadge className="me-2 text-primary" />Employee Hierarchy</h2>
        <p className="text-muted mb-0">Filtered Workday employee hierarchy with employee, position, department, and manager fields.</p>
      </div>

      <Form onSubmit={applyFilters} className="mb-3">
        <div className="d-flex gap-2 flex-wrap align-items-end">
          <Form.Group style={{ minWidth: 320, flex: '1 1 320px' }}>
            <Form.Label className="small text-muted mb-1">Search</Form.Label>
            <InputGroup>
              <InputGroup.Text><FaSearch /></InputGroup.Text>
              <Form.Control
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, email, employee number, position, manager"
              />
            </InputGroup>
          </Form.Group>
          <Form.Group style={{ width: 190 }}>
            <Form.Label className="small text-muted mb-1">Employee Type</Form.Label>
            <Form.Select value={employeeType} onChange={(e) => setEmployeeType(e.target.value)}>
              <option value="">All types</option>
              {employeeTypeOptions.map((type) => <option key={type} value={type}>{type}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group style={{ width: 190 }}>
            <Form.Label className="small text-muted mb-1">Department ID</Form.Label>
            <Form.Select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
              <option value="">All departments</option>
              {departmentOptions.map((dept) => <option key={dept} value={dept}>{dept}</option>)}
            </Form.Select>
          </Form.Group>
          <Button type="submit" variant="primary">Apply</Button>
          <Button type="button" variant="outline-secondary" onClick={clearFilters}>Clear</Button>
          <Button type="button" variant="outline-primary" onClick={load}><FaSyncAlt className="me-1" />Refresh</Button>
        </div>
      </Form>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <>
          <div className="table-responsive">
            <Table hover size="sm" className="spa-table align-middle">
              <thead>
                <tr>
                  <th><SortHeader label="Name" column="name" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                  <th><SortHeader label="Email" column="email" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                  <th><SortHeader label="Employee" column="employeeNum" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                  <th><SortHeader label="Position" column="position" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                  <th><SortHeader label="Department" column="department" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                  <th><SortHeader label="Type" column="type" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                  <th><SortHeader label="Dates" column="startDate" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                  <th><SortHeader label="Manager" column="manager" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                  <th><SortHeader label="Loaded" column="loadDate" sortBy={sortBy} sortDirection={sortDirection} onSort={sort} /></th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map((u) => {
                  const userIdentifier = u.employeeEmail || u.employeeNum || u.employeeWindowsNtid;
                  return (
                    <tr key={u.employeeHierarchyId} className="spa-click-row" onClick={() => openUser(userIdentifier)}>
                      <td className="fw-semibold text-nowrap">
                        {[u.employeeFirstName, u.employeeLastName].filter(Boolean).join(' ') || '-'}
                        <FaExternalLinkAlt className="ms-2 text-muted small" />
                      </td>
                      <td>{u.employeeEmail || '-'}</td>
                      <td>
                        <div>{u.employeeNum || '-'}</div>
                        {u.employeeWindowsNtid && <div className="text-muted small">{u.employeeWindowsNtid}</div>}
                      </td>
                      <td style={{ minWidth: 220 }}>{u.employeePositionTitle || '-'}</td>
                      <td>{u.employeeDeptId || '-'}</td>
                      <td>{u.employeeType ? <Badge bg="info">{u.employeeType}</Badge> : '-'}</td>
                      <td className="text-nowrap small">
                        <div>Start: {fmtDate(u.employeeStartDate)}</div>
                        <div className="text-muted">End: {fmtDate(u.employeeEndDate)}</div>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 align-baseline"
                          onClick={(e) => { e.stopPropagation(); openUser(u.managerEmpId || u.managerWindowsNtid); }}
                          disabled={!u.managerEmpId && !u.managerWindowsNtid}
                        >
                            <FaUserTie className="me-1" />{[u.managerFirstName, u.managerLastName].filter(Boolean).join(' ') || u.managerEmpId || '-'}
                        </button>
                          {u.managerEmail && <div className="text-muted small">{u.managerEmail}</div>}
                          {u.managerPositionTitle && <div className="text-muted small">{u.managerPositionTitle}</div>}
                        {u.managerWindowsNtid && <div className="text-muted small">{u.managerWindowsNtid}</div>}
                      </td>
                      <td className="text-nowrap text-muted">{fmt(u.loadDate)}</td>
                    </tr>
                  );
                })}
                {data?.items.length === 0 && (
                  <tr><td colSpan={9} className="text-center text-muted py-5">No employee hierarchy records found</td></tr>
                )}
              </tbody>
            </Table>
          </div>
          {data && <PaginationBar page={page} pageSize={PAGE_SIZE} total={data.totalCount} onChange={setPage} />}
        </>
      )}
    </Container>
  );
};

export default EmployeeHierarchyPage;
